import React, { useMemo, useState } from "react";
import Chart from "react-apexcharts";

// ابزار ساده برای جداکننده هزارگان (ریال)
const formatIRR = (n) =>
    (n ?? 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const ranges = [
    { key: "24h", label: "۲۴ ساعت" },
    { key: "7d", label: "۷ روز" },
    { key: "30d", label: "۳۰ روز" },
];

// نمونه داده (بعداً از API خودت پر کن)
const sample = {
    "24h": Array.from({ length: 24 }, (_, i) => ({
        x: `${i}:00`,
        y: 160000 + Math.round(Math.sin(i / 3) * 5000 + Math.random() * 2000),
    })),
    "7d": ["۲۶ دی", "۲۷ دی", "۲۸ دی", "۲۹ دی", "۳۰ دی", "۱ بهمن", "۲ بهمن"].map(
        (d, i) => ({
            x: d,
            y: 155000 + Math.round(Math.sin(i / 1.4) * 12000 + Math.random() * 4000),
        })
    ),
    "30d": Array.from({ length: 30 }, (_, i) => ({
        x: `دی ${i + 1}`,
        y:
            150000 +
            Math.round(Math.sin(i / 4) * 18000 + (i * 800) / 2 + Math.random() * 5000),
    })),
};

export default function DigikalaGoldChart() {
    const [range, setRange] = useState("30d");

    const seriesData = sample[range];

    const stats = useMemo(() => {
        const ys = seriesData.map((p) => p.y);
        const min = Math.min(...ys);
        const max = Math.max(...ys);
        const first = ys[0];
        const last = ys[ys.length - 1];
        const changePct = first ? ((last - first) / first) * 100 : 0;
        return { min, max, changePct };
    }, [seriesData]);

    const options = useMemo(
        () => ({
            chart: {
                type: "area",
                toolbar: { show: false },
                zoom: { enabled: false },
            },
            stroke: {
                curve: "straight", // تو ویدئو خط خیلی smooth نبود
                width: 2,
            },
            dataLabels: { enabled: false },
            grid: {
                borderColor: "#E9EEF2",
                strokeDashArray: 4,
                padding: { left: 8, right: 8 },
            },
            fill: {
                type: "gradient",
                gradient: {
                    shadeIntensity: 0,
                    opacityFrom: 0.35,
                    opacityTo: 0.05,
                    stops: [0, 90, 100],
                },
            },
            tooltip: {
                x: { show: true },
                y: {
                    formatter: (val) => `${formatIRR(Math.round(val))} ریال`,
                },
            },
            xaxis: {
                categories: seriesData.map((p) => p.x),
                labels: {
                    style: { colors: "#7A8793" },
                    rotate: 0,
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
            },
            yaxis: {
                labels: {
                    style: { colors: "#7A8793" },
                    formatter: (val) => formatIRR(Math.round(val)),
                },
            },
            colors: ["#0E8F86"], // سبز-آبی شبیه دیجی‌کالا
        }),
        [seriesData]
    );

    const series = useMemo(
        () => [{ name: "قیمت", data: seriesData.map((p) => p.y) }],
        [seriesData]
    );

    return (
        <div
            style={{
                background: "#fff",
                borderRadius: 16,
                padding: 16,
                border: "1px solid #EEF2F5",
                maxWidth: 780,
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ fontWeight: 700, color: "#4B5563" }}>
                    قیمت طلا (ریال)
                </div>

                <div style={{ marginRight: "auto", display: "flex", gap: 8 }}>
                    {ranges.map((r) => {
                        const active = r.key === range;
                        return (
                            <button
                                key={r.key}
                                onClick={() => setRange(r.key)}
                                style={{
                                    borderRadius: 999,
                                    padding: "6px 12px",
                                    border: `1px solid ${active ? "#0E8F86" : "#D7E2EA"}`,
                                    background: active ? "#0E8F86" : "#fff",
                                    color: active ? "#fff" : "#1F2937",
                                    cursor: "pointer",
                                    fontWeight: 600,
                                    fontSize: 12,
                                }}
                            >
                                {r.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div style={{ marginTop: 10 }}>
                <Chart options={options} series={series} type="area" height={260} />
            </div>

            <div
                style={{
                    marginTop: 8,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 10,
                    alignItems: "center",
                    borderTop: "1px solid #EEF2F5",
                    paddingTop: 12,
                }}
            >
                <div>
                    <div style={{ fontSize: 12, color: "#6B7280" }}>بالاترین قیمت</div>
                    <div style={{ fontWeight: 800, marginTop: 4 }}>
                        {formatIRR(stats.max)} ریال
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: 12, color: "#6B7280" }}>پایین‌ترین قیمت</div>
                    <div style={{ fontWeight: 800, marginTop: 4 }}>
                        {formatIRR(stats.min)} ریال
                    </div>
                </div>
                <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: 12, color: "#6B7280" }}>تغییرات</div>
                    <div
                        style={{
                            fontWeight: 900,
                            marginTop: 4,
                            color: stats.changePct >= 0 ? "#16A34A" : "#DC2626",
                        }}
                    >
                        {stats.changePct >= 0 ? "↑" : "↓"} {stats.changePct.toFixed(2)}%
                    </div>
                </div>
            </div>

            <button
                style={{
                    marginTop: 14,
                    width: "100%",
                    borderRadius: 12,
                    padding: "10px 12px",
                    border: "1px solid #0E8F86",
                    background: "#fff",
                    color: "#0E8F86",
                    fontWeight: 800,
                    cursor: "pointer",
                }}
            >
                تاریخچه خرید و فروش
            </button>
        </div>
    );
}
